import React, { createContext, useContext, useState, useCallback } from "react";
import { WorkflowType } from "types";

type WorkflowContextType<T = any> = {
  flows: WorkflowType[]; // The flows still hold the workflow steps and flow state
  data: T; // Separate the data from the flow, making it independent
  current: WorkflowType; // The current workflow state
  pushFlow: (flow: WorkflowType) => void;
  popFlow: () => void;
  updateData: <T>(path: string, value: any) => void | Promise<void>;
  nextStep: () => void;
  prevStep: () => void;
  setData: (data: T) => void; // Setter for updating the data
};

export const WorkflowContext = createContext<WorkflowContextType<any>>({
  flows: [],
  data: {}, // Initialize an empty object for the data
  current: null,
  pushFlow: () => {},
  popFlow: () => {},
  updateData: () => {},
  nextStep: () => {},
  prevStep: () => {},
  setData: () => {},
});

export const WorkflowProvider: React.FC<{
  initData: any;
  children: React.ReactNode;
}> = ({ initData, children }) => {
  const [flows, setFlows] = useState<WorkflowType[]>([]);
  const [data, setData] = useState<any>(initData); // State to hold the data

  const pushFlow = (flow: WorkflowType) => {
    setFlows((prev) => [...prev, flow]);
  };

  const popFlow = () => {
    setFlows((prev) => prev.slice(0, -1));
  };

  const updateData = async <T,>(path: string, value: any): Promise<void> => {
    return new Promise((resolve) => {
      setData((prevData) => {
        if (path === "") {
          resolve(value);
          return value;
        }

        if (!path.includes(".")) {
          const updatedData = { ...prevData, [path]: value };
          resolve(updatedData);
          return updatedData;
        }

        const updated = structuredClone(prevData);
        // const updated = JSON.parse(JSON.stringify(prevData));
        const keys = path.split(".");
        let current = updated;

        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          if (i === keys.length - 1) {
            current[key] = value;
          } else {
            if (!current[key] || typeof current[key] !== "object") {
              current[key] = {};
            }
            current = current[key];
          }
        }

        resolve(updated);
        return updated;
      });
    });
  };

  const nextStep = () => {
    setFlows((prev) => {
      const updated = [...prev];
      const last = updated.pop()!;
      updated.push({
        ...last,
        step: Math.min(last.step + 1, last.count),
      });
      return updated;
    });
  };

  const prevStep = () => {
    setFlows((prev) => {
      const updated = [...prev];
      const last = updated.pop()!;
      updated.push({ ...last, step: Math.max(last.step - 1, 1) });
      return updated;
    });
  };

  return (
    <WorkflowContext.Provider
      value={{
        flows,
        data,
        current: flows[flows.length - 1] ?? null,
        pushFlow,
        popFlow,
        updateData,
        nextStep,
        prevStep,
        setData,
      }}
    >
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflow = <T = any,>(): WorkflowContextType<T> => {
  const context = useContext(WorkflowContext);
  if (!context)
    throw new Error("useWorkflow must be used within WorkflowProvider");
  return context;
};
